/**
 * What each site still needs, day by day.
 *
 * The coverage window says when a site must be guarded and the required
 * guard count says by how many. Whatever is already on the board counts
 * towards that, so the demand is only what is missing: open places on draft
 * duties, and blank static posts for the hours no duty covers deeply enough.
 * Nothing already rostered is ever moved, and days in the past or already
 * published are left alone.
 */

import type { DutyColour } from '../dutyBoardService';
import { PLANNING_POLICY, tileWindow } from './shiftPatterns';
import { MINUTES_PER_DAY, addDays, eachDate } from './time';
import type {
  DayDemand,
  DemandBlock,
  ExistingSlot,
  ScheduleContext,
  ScheduleOptions,
  ScheduleSiteContext,
  TimeWindow,
} from './types';

/** The name the board gives a post nobody has named yet. */
export const BLANK_POST_NAME = 'Unnamed Post';

const LANE_COLOURS: DutyColour[] = ['sky', 'mint', 'amber', 'rose', 'slate'];

/** A duty holds as many places as it asks for, or as many guards as it already has. */
const placesOn = (slot: ExistingSlot): number => Math.max(slot.guardsRequired, slot.guardIds.length);

/** One place of cover per window, so a duty holding two places appears twice. */
function supplyFor(date: string, slotsByDate: Map<string, ExistingSlot[]>): TimeWindow[] {
  const today = (slotsByDate.get(date) ?? []).map((slot) => ({
    window: { startMinute: slot.startMinute, endMinute: slot.endMinute },
    places: placesOn(slot),
  }));
  // Yesterday's duties that run past midnight cover the start of today.
  const carriedOver = (slotsByDate.get(addDays(date, -1)) ?? [])
    .filter((slot) => slot.endMinute > MINUTES_PER_DAY)
    .map((slot) => ({
      window: { startMinute: Math.max(slot.startMinute - MINUTES_PER_DAY, 0), endMinute: slot.endMinute - MINUTES_PER_DAY },
      places: placesOn(slot),
    }));
  return [...today, ...carriedOver].flatMap(({ window, places }) => Array.from({ length: places }, () => ({ ...window })));
}

/**
 * The stretches of the window short of cover, one list per missing guard: the
 * first holds every stretch at least one guard short, the second every stretch
 * at least two short, and so on.
 */
export function coverageGaps(window: TimeWindow, requiredGuards: number, supply: TimeWindow[]): TimeWindow[][] {
  const cuts = new Set<number>([window.startMinute, window.endMinute]);
  supply.forEach((item) => {
    [item.startMinute, item.endMinute].forEach((minute) => {
      if (minute > window.startMinute && minute < window.endMinute) cuts.add(minute);
    });
  });
  const edges = Array.from(cuts).sort((a, b) => a - b);

  const layers: TimeWindow[][] = Array.from({ length: requiredGuards }, () => []);
  for (let index = 0; index < edges.length - 1; index += 1) {
    const startMinute = edges[index];
    const endMinute = edges[index + 1];
    const covered = supply.filter((item) => item.startMinute <= startMinute && item.endMinute >= endMinute).length;
    const missing = Math.max(requiredGuards - covered, 0);

    for (let layer = 0; layer < missing; layer += 1) {
      const stretches = layers[layer];
      const previous = stretches[stretches.length - 1];
      if (previous && previous.endMinute === startMinute) previous.endMinute = endMinute;
      else stretches.push({ startMinute, endMinute });
    }
  }
  return layers;
}

function openPlaceBlocks(siteId: string, slot: ExistingSlot): DemandBlock[] {
  const open = slot.guardsRequired - slot.guardIds.length;
  return Array.from({ length: Math.max(open, 0) }, (_, index) => ({
    key: `${slot.id}#${index}`,
    siteId,
    slotId: slot.id,
    isNewSlot: false,
    slotDate: slot.slotDate,
    dutyName: slot.name,
    startMinute: slot.startMinute,
    endMinute: slot.endMinute,
    requiredLicenceTypeId: slot.requiredLicenceTypeId,
    lane: 0,
    colour: 'sky' as DutyColour,
  }));
}

function siteDemand(
  { site, rule, today, slots, publishedBreakDates }: ScheduleSiteContext,
  options: ScheduleOptions,
  newId: () => string,
): DayDemand[] {
  const window: TimeWindow = { startMinute: site.coverageStartMinute, endMinute: site.coverageEndMinute };
  const requiredGuards = Math.max(1, Math.floor(site.requiredGuardCount));
  const lockedByBreak = new Set(publishedBreakDates);

  const slotsByDate = new Map<string, ExistingSlot[]>();
  slots.forEach((slot) => {
    const list = slotsByDate.get(slot.slotDate) ?? [];
    list.push(slot);
    slotsByDate.set(slot.slotDate, list);
  });

  return eachDate(options.startDate, options.endDate).map((date): DayDemand => {
    if (date < today) return { siteId: site.id, date, status: 'past', blocks: [] };

    const daySlots = slotsByDate.get(date) ?? [];
    if (lockedByBreak.has(date) || daySlots.some((slot) => slot.isPublished)) {
      return { siteId: site.id, date, status: 'published', blocks: [] };
    }

    const blocks = daySlots.flatMap((slot) => openPlaceBlocks(site.id, slot));

    // A duty counts once per place it holds, filled or not: an open place is
    // demand of its own above, not a reason to draft a second post beside it.
    coverageGaps(window, requiredGuards, supplyFor(date, slotsByDate)).forEach((stretches, lane) => {
      stretches
        .filter((stretch) => stretch.endMinute - stretch.startMinute >= PLANNING_POLICY.minimumDutyMinutes)
        .flatMap((stretch) => tileWindow(stretch, options.preferredShiftMinutes, rule))
        .forEach((shift) => {
          const id = newId();
          blocks.push({
            key: id,
            siteId: site.id,
            slotId: id,
            isNewSlot: true,
            slotDate: date,
            dutyName: BLANK_POST_NAME,
            startMinute: shift.startMinute,
            endMinute: shift.endMinute,
            requiredLicenceTypeId: site.requiredLicenceTypeId,
            lane,
            colour: LANE_COLOURS[lane % LANE_COLOURS.length],
          });
        });
    });

    blocks.sort((a, b) => a.startMinute - b.startMinute || a.lane - b.lane || a.key.localeCompare(b.key));
    return { siteId: site.id, date, status: blocks.length > 0 ? 'planned' : 'covered', blocks };
  });
}

/**
 * Everything to draft across the period, site by site. `newId` supplies the
 * ids of the posts the draft will create, so the dry run and the save name the
 * same rows.
 */
export function buildDemand(
  context: ScheduleContext,
  options: ScheduleOptions,
  newId: () => string,
): DayDemand[] {
  return context.sites.flatMap((siteContext) => siteDemand(siteContext, options, newId));
}
